import type { ReactNode } from "react";

import { InputNumber, type InputNumberProps } from "antd";

import { cn } from "@btnet/utilities-core";

import "./BuiNumberInput.less";

export interface IBuiNumberInputProps extends Omit<InputNumberProps, "value"> {
  /**
   * The label text displayed before (on the left side of) the input field
   */
  addonBefore?: ReactNode;

  /**
   * The label text displayed after (on the right side of) the input field
   */
  addonAfter?: ReactNode;

  /**
   * Hide the up/down arrow spin buttons on number inputs
   */
  hideSpinButtons?: boolean;

  /**
   * Test id applied to the underlying input as `data-testid`
   */
  "data-testid": string;

  /**
   * The current value of the input. Accepts a number, or a string for
   * partially-typed / formatted input. `null` or `undefined` renders the input empty.
   */
  value: number | string | null | undefined;
}

/**
 * BuiNumberInput - a styled wrapper around antd's InputNumber.
 * Applies Blueprint design tokens via BuiNumberInput.less (input base, focus ring,
 * disabled/hover states, addon-group spacing, and the hide-spin-buttons rule).
 * Adds optional before/after addons, a spin-button toggle, and a required data-testid.
 * All other antd InputNumberProps are forwarded to the underlying input.
 * Behavior (read-only/disabled display, tracking) lives in the app-level
 * BTNumberInput; this component is styling only.
 * @param props - {@link IBuiNumberInputProps} The component props.
 * @param props.addonBefore - Node rendered to the left of the input; enables the group layout.
 * @param props.addonAfter - Node rendered to the right of the input; enables the group layout.
 * @param props.hideSpinButtons - When true, hides antd's up/down arrows. Defaults to false.
 * @param props.data-testid - Test ID applied to the underlying input.
 * @param props.value - Current value; when null or undefined the input renders empty.
 */
export const BuiNumberInput = ({
  addonBefore,
  addonAfter,
  hideSpinButtons = false,
  value,
  ...rest
}: IBuiNumberInputProps) => {
  const component = (
    <InputNumber
      {...rest}
      {...(value !== undefined && value !== null && { value: value })}
      style={
        addonBefore || addonAfter
          ? { ...rest.style }
          : { width: "100%", ...rest.style }
      }
      // TODO (ado-231821): drop the hide-spin-buttons CSS and pass controls={false} once we upgrade antd
      className={cn({ "hide-spin-buttons": hideSpinButtons }, rest.className)}
      aria-disabled={rest.disabled}
    />
  );

  if (addonBefore || addonAfter) {
    return (
      <span className="bui-number-input">
        <span className="ant-input-group-wrapper">
          <span
            className={cn("ant-input-wrapper ant-input-group", {
              before: !!addonBefore,
            })}
          >
            {addonBefore && (
              <span className="ant-input-group-addon">{addonBefore}</span>
            )}
            {component}
            {addonAfter && (
              <span className="ant-input-group-addon">{addonAfter}</span>
            )}
          </span>
        </span>
      </span>
    );
  }

  return <span className="bui-number-input">{component}</span>;
};
